import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';

/**
 * Global order-flow settings, read once per session and cached.
 *
 * Mirrors CoilSettingsContext. Readable by every role, changeable only by a super
 * admin (enforced by the API).
 *
 * Note what this context does NOT do: it does not hide the Confirm button anywhere.
 * That button already renders only for orders still in PENDING, and with the toggle on
 * a new order comes back already confirmed, so the button disappears on its own. An
 * order the backend could not confirm — a coil-tracked line with no coils picked —
 * stays PENDING and keeps its button, which is exactly the intended fallback. Gating
 * the button on this flag would take it away from precisely the orders that need it.
 *
 * It fails closed like the coil context: an unreachable endpoint reads as OFF, which is
 * the long-standing behaviour rather than a claim about a backend we could not reach.
 */

const api = axios.create({ baseURL: `${API_BASE_URL}/api/v1` });
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CACHE_TTL_MS = 60 * 1000;

// The only order-settings key the UI reads today.
const SKIP_ORDER_CONFIRMATION_KEY = 'skip_order_confirmation';

const OrderSettingsContext = createContext(null);

export const OrderSettingsProvider = ({ children }) => {
  const [skipOrderConfirmation, setSkipOrderConfirmation] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);

  const fetchSettings = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetchRef.current < CACHE_TTL_MS) return;
    try {
      const res = await api.get('/order-settings');
      // A missing row reads as disabled — an environment that has not run migration
      // 049 behaves exactly as it always did.
      const row = (res.data || []).find((item) => item?.key === SKIP_ORDER_CONFIRMATION_KEY);
      setSkipOrderConfirmation(Boolean(row?.enabled));
      lastFetchRef.current = Date.now();
    } catch {
      // lastFetchRef is left untouched so the next read retries rather than caching
      // the failure for 60s.
      setSkipOrderConfirmation(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(true); }, [fetchSettings]);

  /**
   * Turn the skip-confirmation setting on or off. Super admin only (enforced by the
   * API, which rejects everyone else — let the caller surface that error).
   *
   * Affects orders created from this moment on. Orders already sitting in PENDING keep
   * their Confirm button; nothing retro-confirms them.
   *
   * @param {boolean} enabled
   */
  const updateSkipOrderConfirmation = useCallback(async (enabled) => {
    const res = await api.put(`/order-settings/${SKIP_ORDER_CONFIRMATION_KEY}`, { enabled });
    setSkipOrderConfirmation(Boolean(res.data?.enabled));
    lastFetchRef.current = 0; // invalidate cache
    return res.data;
  }, []);

  return (
    <OrderSettingsContext.Provider value={{
      skipOrderConfirmation,
      loading,
      updateSkipOrderConfirmation,
      refetch: () => fetchSettings(true),
    }}>
      {children}
    </OrderSettingsContext.Provider>
  );
};

export const useOrderSettings = () => {
  const ctx = useContext(OrderSettingsContext);
  if (!ctx) throw new Error('useOrderSettings must be used inside OrderSettingsProvider');
  return ctx;
};
